import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChatDotsIcon, CheckCircleIcon, EyeIcon, XCircleIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { IconButton, LineItem, TextArea, UploadImageArea } from '@reveni-io/reveni-ui'

import ImagePlus from '../../../../public/icons/image_plus.svg?react'
import useLineItemToValidate from './hooks/useLineItemToValidate/useLineItemToValidate'
import ReasonSelector from './ReasonSelector/ReasonSelector'
import { defaultOptions } from './constants'
import * as Styles from './styles'

const LineItemToValidate = ({
  lineItem,
  onChangeStatus,
  onChangeReason,
  onChangeNotes,
  onChangeImages,
  onDeleteImage,
  options = defaultOptions,
}) => {
  const theme = useTheme()
  const finalOptions = { ...defaultOptions, ...options }
  const { shouldShowLineItemOptions, isActive, lineItemReason, lineItemNotes } = useLineItemToValidate({
    lineItem,
    showLineItemOptions: finalOptions.showLineItemOptions,
  })
  const [showImageUploadArea, setShowImageUploadArea] = useState(lineItem?.images?.length > 0)
  const { t } = useTranslation()

  const toggleImageUploadArea = () => {
    setShowImageUploadArea(showImageUploadArea => !showImageUploadArea)
  }

  return (
    <Styles.Container>
      <Styles.LineItemContainer>
        <Styles.IconsContainer>
          {finalOptions.showActions && (
            <>
              <div>
                <IconButton
                  variant={isActive(finalOptions.statusForAccepted) ? 'contained' : 'outlined'}
                  color="success"
                  hoverText={t(finalOptions.acceptLabel)}
                  onClick={() => onChangeStatus(lineItem.id, finalOptions.statusForAccepted)}
                  icon={<CheckCircleIcon size={20} />}
                />
              </div>
              <div>
                <IconButton
                  variant={isActive(finalOptions.statusForRejected) ? 'contained' : 'outlined'}
                  color="danger"
                  hoverText={t(finalOptions.rejectLabel)}
                  onClick={() => onChangeStatus(lineItem.id, finalOptions.statusForRejected)}
                  icon={<XCircleIcon size={20} />}
                />
              </div>
              {finalOptions?.showForReviewButton && (
                <div>
                  <IconButton
                    variant={isActive(finalOptions.statusForForReview) ? 'contained' : 'outlined'}
                    color="warning"
                    hoverText={t('Por revisar')}
                    onClick={() => onChangeStatus(lineItem.id, finalOptions.statusForForReview)}
                    icon={<EyeIcon size={20} />}
                  />
                </div>
              )}
            </>
          )}
        </Styles.IconsContainer>
        <Styles.LineItemsContainer>
          <LineItem key={lineItem.id} item={{ ...lineItem, reject_reason_text: null, status: null }} />
          {lineItem?.bundleItems?.map(item => (
            <LineItem key={item.id} item={{ ...item, reject_reason_text: null, status: null }} />
          ))}
        </Styles.LineItemsContainer>
      </Styles.LineItemContainer>
      {shouldShowLineItemOptions && (
        <Styles.OptionsContainer>
          <ReasonSelector
            status={lineItem.newStatus}
            selectedReason={lineItemReason}
            onChange={e => onChangeReason(lineItem.id, e.target.value)}
          />
          <Styles.NotesContainer>
            <TextArea
              backgroundColor="white"
              fullWidth
              placeholder={t('Notas')}
              minRows={1}
              maxLength={250}
              showCounter
              value={lineItemNotes}
              onChange={e => onChangeNotes(lineItem.id, e.target.value)}
              startAdornment={
                <Styles.IconContainer>
                  <ChatDotsIcon weight="light" color={theme.colors.text.subtle} size={16} />
                </Styles.IconContainer>
              }
            />
            <div>
              <IconButton
                onClick={toggleImageUploadArea}
                variant="text"
                icon={<ImagePlus data-testid="uploadImage" size={24} />}
              />
            </div>
          </Styles.NotesContainer>
          {showImageUploadArea && (
            <UploadImageArea
              backgroundColorArea="white"
              images={lineItem?.images?.filter(image => [lineItem.newStatus, 'new'].includes(image.status))}
              onSelectImages={images => onChangeImages(lineItem.id, images)}
              onDeleteImage={imageId => onDeleteImage(lineItem.id, imageId)}
            />
          )}
        </Styles.OptionsContainer>
      )}
    </Styles.Container>
  )
}

export default LineItemToValidate
